import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ACCOUNT_TYPES, CORPORATE_TYPES } from '../../../constants/constTypes';
import { accountRoutes } from '../../../constants/routes';
import { selectAccountData } from '../../../redux/reducers/account/account';
import { useIsAdmin } from '../../admin/useIsAdmin';

const LINKS = {
    myCompany: { name: 'myCompany', to: accountRoutes.COMPANY, icon: 'dealer' },
    transportForSell: {
        name: 'transportForSell',
        to: accountRoutes.GARAGE_CAR,
        matchPrefix: 'garage',
        icon: 'car',
    },
    transportForRent: {
        name: 'transportForRent',
        to: accountRoutes.RENT_CAR,
        matchPrefix: 'rent',
        icon: 'money',
    },
    parts: {
        name: 'parts',
        to: accountRoutes.PARTS_CAR,
        matchPrefix: 'parts',
        icon: 'parts',
    },
    placements: {
        name: 'placements',
        to: accountRoutes.PLACEMENT,
        icon: 'placement',
    },
    services: { name: 'services', to: accountRoutes.SERVICES, icon: 'service' },
    notebook: {
        name: 'notebook',
        to: accountRoutes.FAVORITE_CAR,
        matchPrefix: 'favorite',
        icon: 'documentCheck',
    },
    settings: { name: 'settings', to: accountRoutes.SETTINGS, icon: 'gear' },
    adminImport: { name: 'adminImport', to: accountRoutes.IMPORT, icon: 'copyLink' },
};

const getAccountLinkKeys = ({ accountType, typeCorporate, hasDisassembly }) => {
    if (accountType === ACCOUNT_TYPES.USER) {
        return ['transportForSell', 'transportForRent', 'parts', 'placements'];
    }

    const isDealer = accountType === ACCOUNT_TYPES.DEALER;
    if (!isDealer && accountType !== ACCOUNT_TYPES.CORPORATE) return [];
    if (!typeCorporate) return ['myCompany'];

    const keys = ['myCompany'];
    if (isDealer || typeCorporate === CORPORATE_TYPES.AUTOCOMPANY) {
        keys.push('transportForSell', 'transportForRent');
    }
    if (typeCorporate === CORPORATE_TYPES.RENTCOMPANY) {
        keys.push('transportForRent', 'parts');
    }
    if (typeCorporate === CORPORATE_TYPES.PARTSCOMPANY) {
        keys.push('parts');
    }
    keys.push('services');
    if (typeCorporate === CORPORATE_TYPES.SERVICES) {
        keys.push('placements');
    }
    if (hasDisassembly) {
        keys.push('parts');
    }

    return [...new Set(keys)];
};

export const useCabinetMenuLinks = () => {
    const account = useSelector(selectAccountData) || {};
    const accountType = account.accountType;
    const typeCorporate = account.company?.typeCorporate;
    const hasDisassembly = Boolean(account.hasDisassembly);
    const isAdmin = useIsAdmin();

    return useMemo(
        () =>
            [
                ...getAccountLinkKeys({
                    accountType,
                    typeCorporate,
                    hasDisassembly,
                }),
                'notebook',
                'settings',
                ...(isAdmin ? ['adminImport'] : []),
            ].map((key) => LINKS[key]),
        [accountType, typeCorporate, hasDisassembly, isAdmin],
    );
};
